import { collection, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { MemberSchema, type Member } from '@etiquetachef/types';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';

const TAG = 'admin/firestoreMembers';

/** Estado da aba Usuários do detalhe do tenant. */
export type MemberListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; members: Member[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Lista os membros (usuários) do tenant. O doc id é o `uid`; o `tenantId` vem
 * do path (membros legados podem não ter o campo gravado, então injetamos).
 * Ordena por papel (OWNER → MANAGER → STAFF) e depois nome, no cliente. Docs
 * inválidos no parse Zod ficam logados e ignorados.
 *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 (abas Usuários e
 * Responsáveis na página do tenant).
 *
 * Contrato: listener em `tenants/{tenantId}/members`; erro de snapshot vira `ERROR` (a tela mostra o alerta).
 * Devolve o unsubscribe.
 *
 * Usado por: `features/members/useMembers.ts`.
 *
 * Arquitetura: rule `match /tenants/{tenantId}` → `match /members/{memberUid}`
 * (super-admin lê). Escrita de membro só por callable — o admin aqui só lê.
 *
 * Dados — `tenants/{tid}/members/{uid}` (schema `MemberSchema`):
 *   { "role": "OWNER", "displayName": "Fulano", "phoneE164": "+5511900000000", … }
 * `tenantId` e `uid` vêm do caminho (membro legado não tem o campo).
 *
 * Falhas conhecidas:
 *  - Membro fora do schema some da aba só com `console.warn`, sem report na
 *    telemetria (#926, achado da frente de docstrings).
 */
export function subscribeMembers(
  tenantId: string,
  onResult: (r: MemberListResult) => void,
): Unsubscribe {
  console.info(`${TAG}: subscribe tenant=${tenantId}`);
  const ref = collection(firestore, 'tenants', tenantId, 'members');
  return onSnapshot(
    ref,
    (snap) => {
      const members: Member[] = [];
      for (const d of snap.docs) {
        const parsed = MemberSchema.safeParse({ tenantId, uid: d.id, ...snapshotData(d) });
        if (parsed.success) {
          members.push(parsed.data);
        } else {
          console.warn(`${TAG}: member ${d.id} ignorado`, parsed.error.issues);
        }
      }
      const rank: Record<string, number> = { OWNER: 0, MANAGER: 1, STAFF: 2 };
      members.sort((a, b) => {
        const r = (rank[a.role] ?? 9) - (rank[b.role] ?? 9);
        return r !== 0 ? r : a.displayName.localeCompare(b.displayName);
      });
      onResult({ kind: 'READY', members });
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
